/**
 * Lógica pura de la Ficha y la Vista por función (Sprint 286 E; REQ-MINT-006
 * §2; anexo §2.2).
 *
 * - «Apto para»: evaluación **fail-closed** de los requisitos de cada función
 *   contra las capacidades publicadas (con procedencia) y los umbrales de
 *   ámbito. Sin dato ⇒ `no_verificado` (nunca se asume que cumple).
 * - Oferta más económica: mínimo de la tarifa propia entre las ofertas del
 *   mismo `model_ref`; sin tarifa ⇒ `null` (jamás una cifra estimada).
 * - Telemetría por función: agregación de las celdas de rollup (nunca estima).
 * - Deltas de alternativas: respecto a un modelo de referencia; sin dato ⇒ null.
 */

import type {
  BenchmarkHistoryRow,
  DomainAggregate,
  FunctionItem,
  ObservabilityCell,
  OfferingCard,
  OwnBenchRun,
  SheetObservation,
} from "../types";

export type FitVerdict = "apto" | "no_apto" | "no_verificado";

export interface FitReason {
  code:
    "unverified" | "limit" | "capabilityMissing" | "modalities" | "threshold";
  field: string;
  min?: number;
  value?: number | string;
}

export interface FunctionFit {
  code: string;
  name: string;
  state: string;
  verdict: FitVerdict;
  reasons: FitReason[];
}

/**
 * Pares requisito booleano → campo de capacidad (vocabulario idéntico al
 * evaluador del backend, `consultation.REQUIREMENT_FIELDS`).
 */
const BOOL_REQUIREMENTS: Array<[string, string]> = [
  ["require_tools", "tools"],
  ["require_reasoning", "reasoning"],
  ["require_streaming", "streaming"],
  ["require_parallel_tools", "tools_parallel"],
  ["require_structured_output", "structured_output"],
];

/** Evalúa si un modelo (una oferta publicada) es apto para una función. */
export function evaluateFunctionFit(
  fn: Pick<
    FunctionItem,
    "code" | "name" | "state" | "required_capabilities" | "quality_thresholds"
  >,
  options: {
    capabilities: Record<string, unknown>;
    published: Set<string>;
    domains: DomainAggregate[];
  },
): FunctionFit {
  const { capabilities, published, domains } = options;
  const reasons: FitReason[] = [];
  let hardFail = false;

  const merged: Record<string, unknown> = {};
  for (const entry of fn.required_capabilities ?? []) {
    if (entry && typeof entry === "object" && !Array.isArray(entry)) {
      Object.assign(merged, entry as Record<string, unknown>);
    }
  }

  const minContext = merged["min_context_window"];
  if (typeof minContext === "number") {
    const value = capabilities["context_window"];
    if (!published.has("context_window") || typeof value !== "number") {
      reasons.push({ code: "unverified", field: "context_window" });
    } else if (value < minContext) {
      hardFail = true;
      reasons.push({
        code: "limit",
        field: "context_window",
        min: minContext,
        value,
      });
    }
  }

  for (const [key, field] of BOOL_REQUIREMENTS) {
    const wanted = merged[key];
    if (typeof wanted !== "boolean") continue;
    const value = capabilities[field];
    if (!published.has(field) || typeof value !== "boolean") {
      reasons.push({ code: "unverified", field });
    } else if (wanted !== value) {
      hardFail = true;
      reasons.push({ code: "capabilityMissing", field, value: String(value) });
    }
  }

  const modalities = merged["required_modalities"];
  if (Array.isArray(modalities) && modalities.length > 0) {
    const value = capabilities["modalities"];
    if (!published.has("modalities") || !Array.isArray(value)) {
      reasons.push({ code: "unverified", field: "modalities" });
    } else {
      const missing = (modalities as unknown[]).filter(
        (item) => !value.includes(item),
      );
      if (missing.length > 0) {
        hardFail = true;
        reasons.push({
          code: "modalities",
          field: "modalities",
          value: missing.join(", "),
        });
      }
    }
  }

  const thresholds = fn.quality_thresholds?.["domain_min"];
  if (
    thresholds &&
    typeof thresholds === "object" &&
    !Array.isArray(thresholds)
  ) {
    for (const [domain, min] of Object.entries(
      thresholds as Record<string, unknown>,
    )) {
      const score = domains.find((item) => item.domain === domain)?.score;
      if (typeof score !== "number") {
        reasons.push({ code: "unverified", field: `domain:${domain}` });
      } else if (typeof min === "number" && score < min) {
        hardFail = true;
        reasons.push({
          code: "threshold",
          field: `domain:${domain}`,
          min,
          value: score,
        });
      }
    }
  }

  const verdict: FitVerdict = hardFail
    ? "no_apto"
    : reasons.length > 0
      ? "no_verificado"
      : "apto";
  return { code: fn.code, name: fn.name, state: fn.state, verdict, reasons };
}

export interface CheapestOffering {
  sheetId: string;
  provider: string;
  region: string;
  providerModelId: string;
  input: number;
  output: number | null;
}

/** Oferta con menor **precio cliente** de entrada (calculado en backend). */
export function cheapestOffering(
  offerings: OfferingCard[],
): CheapestOffering | null {
  let best: CheapestOffering | null = null;
  for (const offering of offerings) {
    const price = offering.client_price;
    const input = price?.components?.["input"]?.usd_per_million;
    if (price?.state !== "available" || typeof input !== "number") continue;
    const candidate: CheapestOffering = {
      sheetId: offering.offering.sheet_id,
      provider: offering.offering.provider,
      region: offering.offering.region,
      providerModelId: offering.offering.provider_model_id,
      input,
      output: price.components?.["output"]?.usd_per_million ?? null,
    };
    if (!best || candidate.input < best.input) best = candidate;
  }
  return best;
}

export interface FunctionUsageRow {
  functionCode: string;
  /** Presente cuando la celda es un agregado anónimo por forma de consumo. */
  usageMethod?: string | null;
  tasks: number;
  tokensIn: number;
  tokensOut: number;
  periodFrom: string;
  periodTo: string;
  latencyP95: number | null;
  errorRate: number | null;
}

/** Resumen de uso anónimo de un conjunto de celdas (coste con cobertura). */
export interface UsageSummary {
  tasks: number;
  cost: number;
  coverage: number;
}

export function usageSummary(cells: ObservabilityCell[]): UsageSummary {
  const tasks = cells.reduce((acc, cell) => acc + (cell.tasks_count ?? 0), 0);
  const cost = cells.reduce((acc, cell) => acc + (cell.cost ?? 0), 0);
  const known = cells.reduce(
    (acc, cell) => acc + (cell.coverage ?? 0) * (cell.tasks_count ?? 0),
    0,
  );
  return { tasks, cost, coverage: tasks > 0 ? known / tasks : 0 };
}

/** Agrega celdas de rollup por función (sumas; p95/error de la última celda). */
export function telemetryByFunction(
  cells: ObservabilityCell[],
): FunctionUsageRow[] {
  const grouped = new Map<string, ObservabilityCell[]>();
  for (const cell of cells) {
    // Las celdas de CP no tienen `function_code`: la dimensión es la forma de
    // consumo. Nunca se agrupan todas bajo «unknown».
    const key = cell.function_code ?? cell.usage_method ?? 'unknown';
    const list = grouped.get(key) ?? [];
    list.push(cell);
    grouped.set(key, list);
  }
  const rows: FunctionUsageRow[] = [];
  for (const [functionCode, list] of grouped) {
    const ordered = [...list].sort((a, b) =>
      a.period_start < b.period_start ? 1 : -1,
    );
    const tasks = list.reduce((acc, cell) => acc + cell.tasks_count, 0);
    rows.push({
      functionCode,
      usageMethod: list[0].function_code ? null : list[0].usage_method ?? null,
      tasks,
      tokensIn: list.reduce((acc, cell) => acc + cell.tokens_in, 0),
      tokensOut: list.reduce((acc, cell) => acc + cell.tokens_out, 0),
      periodFrom: ordered[ordered.length - 1].period_start,
      periodTo: ordered[0].period_start,
      latencyP95: ordered[0].latency_p95 ?? null,
      errorRate: ordered[0].error_rate ?? null,
    });
  }
  return rows.sort((a, b) => b.tasks - a.tasks);
}

/** Delta absoluto `alt − ref`; sin dato ⇒ `null` (nunca se estima). */
export function scoreDelta(
  ref: number | null,
  alt: number | null,
): number | null {
  if (ref === null || alt === null) return null;
  return Math.round((alt - ref) * 100) / 100;
}

/** Delta porcentual de **precio** `(alt − ref) / ref`; sin dato ⇒ `null`. */
export function priceDeltaPct(
  ref: number | null,
  alt: number | null,
): number | null {
  if (ref === null || alt === null || ref === 0) return null;
  return Math.round(((alt - ref) / ref) * 10000) / 100;
}

/** Puntos del radar de ámbitos (solo valores presentes). */
export function radarPoints(
  domains: DomainAggregate[],
): Array<{ domain: string; score: number }> {
  return domains
    .filter((domain) => typeof domain.score === "number")
    .map((domain) => ({ domain: domain.domain, score: domain.score }));
}

// ---------------------------------------------------------------------------
// Línea temporal de benchmarks y cambios de campos (Sprint 314; REQ-MINT-006/001)
// ---------------------------------------------------------------------------

export interface BenchmarkTimelineOption {
  code: string;
  name: string;
  count: number;
}

export interface BenchmarkTimelinePoint {
  date: string;
  value: number;
  source: string;
  version: string | null;
  stale: boolean;
}

export interface BenchmarkTimeline {
  code: string;
  name: string;
  points: BenchmarkTimelinePoint[];
  latest: BenchmarkTimelinePoint | null;
  oldest: BenchmarkTimelinePoint | null;
  sources: string[];
}

/** Benchmarks con serie disponible (código, nombre y nº de observaciones). */
export function benchmarkTimelineOptions(
  results: BenchmarkHistoryRow[],
): BenchmarkTimelineOption[] {
  const byCode = new Map<string, BenchmarkTimelineOption>();
  for (const row of results) {
    const current = byCode.get(row.benchmark_code);
    if (current) {
      current.count += 1;
    } else {
      byCode.set(row.benchmark_code, {
        code: row.benchmark_code,
        name: row.benchmark_name,
        count: 1,
      });
    }
  }
  return [...byCode.values()].sort(
    (a, b) => b.count - a.count || a.name.localeCompare(b.name),
  );
}

/**
 * Serie histórica de un benchmark (ascendente por fecha de publicación).
 * Sin puntos no hay serie; **nunca** se interpola ni se estima un valor.
 */
export function benchmarkTimeline(
  results: BenchmarkHistoryRow[],
  benchmarkCode: string,
): BenchmarkTimeline {
  const rows = results.filter((row) => row.benchmark_code === benchmarkCode);
  const points = [...rows]
    .sort((a, b) => a.published_at.localeCompare(b.published_at))
    .map((row) => ({
      date: row.published_at,
      value: row.value,
      source: row.source,
      version: row.benchmark_version ?? null,
      stale: row.stale,
    }));
  return {
    code: benchmarkCode,
    name: rows[0]?.benchmark_name ?? benchmarkCode,
    points,
    latest: points.length ? points[points.length - 1] : null,
    oldest: points.length ? points[0] : null,
    sources: [...new Set(points.map((point) => point.source))],
  };
}

export interface FieldChange {
  previousValue: unknown;
  previousObservedAt: string;
}

/**
 * «Cambio desde la última revisión» por campo: la observación más reciente
 * con **valor distinto** al vigente (re-observaciones idénticas no cuentan).
 * Sin cambio previo, el campo no aparece (la UI muestra «—»).
 */
export function fieldChanges(
  history: SheetObservation[],
): Record<string, FieldChange> {
  const byField = new Map<string, SheetObservation[]>();
  for (const observation of history) {
    const list = byField.get(observation.field) ?? [];
    list.push(observation);
    byField.set(observation.field, list);
  }
  const changes: Record<string, FieldChange> = {};
  for (const [field, observations] of byField) {
    const sorted = [...observations].sort((a, b) =>
      b.observed_at.localeCompare(a.observed_at),
    );
    const latest = sorted[0];
    if (!latest) continue;
    const latestValue = JSON.stringify(latest.value);
    const previous = sorted.find(
      (row) => JSON.stringify(row.value) !== latestValue,
    );
    if (previous) {
      changes[field] = {
        previousValue: previous.value,
        previousObservedAt: previous.observed_at,
      };
    }
  }
  return changes;
}

export interface OwnVsPublishedRow {
  code: string;
  name: string;
  own: number;
  published: number;
  delta: number;
  sample: number;
  ciLow: number | null;
  ciHigh: number | null;
  harnessVersion: string;
  publishedSource: string;
  publishedAt: string | null;
}

/**
 * Compara la **medición propia** vigente con el resultado **publicado** del
 * mismo benchmark (V-29). Solo empareja por `benchmark_code` con dato no stale;
 * sin par comparable no hay fila (nunca se estima). Se conserva la run propia
 * más reciente por benchmark.
 */
export function ownVsPublished(
  runs: OwnBenchRun[],
  domains: DomainAggregate[],
): OwnVsPublishedRow[] {
  const published = new Map<
    string,
    { value: number; name: string; source: string; publishedAt: string | null }
  >();
  for (const domain of domains) {
    for (const component of domain.components ?? []) {
      if (component.stale) continue;
      published.set(component.benchmark_code, {
        value: component.value,
        name: component.benchmark_name,
        source: component.source,
        publishedAt: component.published_at ?? null,
      });
    }
  }

  const latest = new Map<string, OwnBenchRun>();
  for (const run of [...runs].sort((a, b) =>
    b.created_at.localeCompare(a.created_at),
  )) {
    if (!latest.has(run.benchmark_code)) latest.set(run.benchmark_code, run);
  }

  const rows: OwnVsPublishedRow[] = [];
  for (const [code, run] of latest) {
    const reference = published.get(code);
    if (!reference || run.status !== "ok" || typeof run.score !== "number")
      continue;
    rows.push({
      code,
      name: reference.name || code,
      own: run.score,
      published: reference.value,
      delta: Math.round((run.score - reference.value) * 100) / 100,
      sample: run.sample_size,
      ciLow: run.score_ci_low ?? null,
      ciHigh: run.score_ci_high ?? null,
      harnessVersion: run.harness_version,
      publishedSource: reference.source,
      publishedAt: reference.publishedAt,
    });
  }
  return rows.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
}
